import { useState } from "react";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCookieBite, faChartLine, faBullhorn, faShieldHalved } from "@fortawesome/free-solid-svg-icons";
import { useConsent, ConsentPrefs } from "../../contexts/ConsentContext";

type CategoryProps = {
  icon: typeof faChartLine;
  title: string;
  desc: string;
  enabled: boolean;
  required?: boolean;
  onToggle?: (v: boolean) => void;
};

const Category = ({ icon, title, desc, enabled, required, onToggle }: CategoryProps) => (
  <div className="flex items-start gap-3 bg-theme-bg p-3">
    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center bg-theme-surface text-signal dark:text-[#8DB4FF]">
      <FontAwesomeIcon icon={icon} className="text-sm" />
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between gap-2">
        <p className="m-0 text-sm font-semibold leading-5 text-theme-text">{title}</p>
        {required ? (
          <span className="inline-flex h-6 shrink-0 items-center bg-theme-surface px-2 font-display text-xs font-semibold text-theme-text-secondary">
            จำเป็น
          </span>
        ) : (
          // A hard-edged switch: the track and the knob are plates too.
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label={title}
            onClick={() => onToggle?.(!enabled)}
            className={`relative h-6 w-11 shrink-0 rounded-[3px] transition-colors duration-150 ${
              enabled ? "bg-signal" : "bg-theme-text-secondary dark:bg-[#2A4479]"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-[2px] bg-white transition-transform duration-150 ${
                enabled ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        )}
      </div>
      <p className="mt-1 mb-0 text-[13px] leading-relaxed text-theme-text-secondary">{desc}</p>
    </div>
  </div>
);

export default function CookieConsent() {
  const { decided, hasHydrated, acceptAll, rejectAll, save } = useConsent();
  const [customizing, setCustomizing] = useState(false);
  const [draft, setDraft] = useState<ConsentPrefs>({ analytics: true, advertising: true });

  if (!hasHydrated || decided) return null;

  return (
    <>
      {/* Subtle scrim behind the panel so it reads as a focused choice (flat:
          no blur). QA scripts hide the banner by these z-[9989]/z-[9990]
          class names. */}
      <div className="fixed inset-0 z-[9989] bg-stage-deep/60" aria-hidden="true" />

      {/* Bottom overlay: full-width bottom-sheet on mobile, centered along the
          bottom edge on larger screens */}
      <div className="fixed inset-x-0 bottom-0 z-[9990] flex justify-center p-0 sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Cookie preferences"
        className="animate-slide-up w-full max-w-lg overflow-hidden bg-theme-card text-theme-text shadow-float sm:rounded-[4px]"
      >
        {/* Header: the panel's title plate (stage navy in both themes) */}
        <div className="flex items-stretch bg-stage-deep text-white">
          <span className="flex w-12 shrink-0 items-center justify-center bg-signal text-lg" aria-hidden="true">
            <FontAwesomeIcon icon={faCookieBite} />
          </span>
          <div className="px-4 py-2.5">
            <h2 className="m-0 font-display text-lg font-extrabold italic leading-[1.45]">การตั้งค่าคุกกี้</h2>
            <p className="m-0 text-xs leading-4 text-[#A9B8DA]">Cookie Preferences</p>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <p className="m-0 text-sm leading-relaxed text-theme-text-secondary">
            เราใช้คุกกี้เพื่อพัฒนาประสบการณ์ใช้งาน วิเคราะห์สถิติ และแสดงโฆษณาที่เกี่ยวข้อง
            คุณสามารถเลือกได้ว่าจะอนุญาตคุกกี้ประเภทใด{" "}
            <Link href="/terms" className="font-medium text-signal underline underline-offset-2 transition-colors hover:text-[#2D78FF] dark:text-[#8DB4FF] dark:hover:text-white">
              นโยบายความเป็นส่วนตัว
            </Link>
          </p>

          {/* Customizable categories */}
          {customizing && (
            <div className="mt-4 flex flex-col gap-2.5">
              <Category
                icon={faShieldHalved}
                title="จำเป็น (Essential)"
                desc="คุกกี้ที่จำเป็นต่อการทำงานของเว็บไซต์ เช่น การเข้าสู่ระบบและธีม ไม่สามารถปิดได้"
                enabled
                required
              />
              <Category
                icon={faChartLine}
                title="วิเคราะห์ (Analytics)"
                desc="ช่วยให้เราเข้าใจการใช้งานเพื่อปรับปรุงเว็บไซต์ (Microsoft Clarity)"
                enabled={draft.analytics}
                onToggle={(v) => setDraft((d) => ({ ...d, analytics: v }))}
              />
              <Category
                icon={faBullhorn}
                title="โฆษณา (Advertising)"
                desc="ใช้เพื่อแสดงโฆษณาที่เกี่ยวข้องกับคุณมากขึ้น (Google AdSense)"
                enabled={draft.advertising}
                onToggle={(v) => setDraft((d) => ({ ...d, advertising: v }))}
              />
            </div>
          )}

          {/* Actions */}
          <div className="mt-5 flex flex-col gap-2">
            {customizing ? (
              <>
                <button
                  type="button"
                  onClick={() => save(draft)}
                  className="btn-signal w-full"
                >
                  บันทึกการตั้งค่า
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={rejectAll}
                    className="inline-flex h-10 flex-1 items-center justify-center bg-theme-surface px-4 font-display text-[15px] font-semibold text-theme-text transition-colors duration-150 hover:bg-theme-border"
                  >
                    ปฏิเสธทั้งหมด
                  </button>
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="inline-flex h-10 flex-1 items-center justify-center bg-theme-surface px-4 font-display text-[15px] font-semibold text-theme-text transition-colors duration-150 hover:bg-theme-border"
                  >
                    ยอมรับทั้งหมด
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="btn-signal w-full"
                >
                  ยอมรับทั้งหมด
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={rejectAll}
                    className="inline-flex h-10 flex-1 items-center justify-center bg-theme-surface px-4 font-display text-[15px] font-semibold text-theme-text transition-colors duration-150 hover:bg-theme-border"
                  >
                    ปฏิเสธทั้งหมด
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomizing(true)}
                    className="inline-flex h-10 flex-1 items-center justify-center bg-theme-surface px-4 font-display text-[15px] font-semibold text-theme-text transition-colors duration-150 hover:bg-theme-border"
                  >
                    ตั้งค่าเอง
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
